/* tslint:disable */
/* eslint-disable */
// @ts-nocheck
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull create'
 *
 * Required dependencies: amplitude-js@^8.21.0
 * Tracking Plan Version: 251
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli
 *
 * [View Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest)
 *
 * [Full Setup Instructions](https://data.amplitude.com/netlify/Netlify/implementation/create)
 */

import amplitude, { AmplitudeClient, Callback, Config } from 'amplitude-js';

export type Environment = 'production' | 'development';

export const ApiKey: Record<Environment, string> = {
  production: 'cebfd07e4b8bce591224aa2ab1e3e096',
  development: '60c433cd2d7a65ed140f33f250a840ea'
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultOptions: ConfigExt = {
  plan: {
    version: '251',
    branch: 'cmparsons/create_project_settings',
    source: 'create',
    versionId: '8c88fb30-3c5a-469b-81d7-c94ac471d714'
  },
  ...{
    ingestionMetadata: {
      sourceName: 'browser-typescript-ampli',
      sourceVersion: '1.0.0'
    }
  }
};

export interface LoadOptionsBase { disabled?: boolean }

export type LoadOptionsWithEnvironment = LoadOptionsBase & { environment: Environment; client?: { options?: Partial<ConfigExt>; }; };
export type LoadOptionsWithApiKey = LoadOptionsBase & { client: { apiKey: string; options?: Partial<ConfigExt>; } };
export type LoadOptionsWithClientInstance = LoadOptionsBase & { client: { instance: AmplitudeClient; } };

export type LoadOptions = LoadOptionsWithEnvironment | LoadOptionsWithApiKey | LoadOptionsWithClientInstance;

export interface StudioRestartContainerClickedProperties {
  /**
   * ID of an Create organization (while Create and Core organizations are different concepts)
   */
  createOrganizationId?: string;
  /**
   * ID of a Create project
   */
  projectId: string;
}

export interface StudioRestartPreviewClickedProperties {
  /**
   * ID of an Create organization (while Create and Core organizations are different concepts)
   */
  createOrganizationId?: string;
  /**
   * ID of a Create project
   */
  projectId: string;
}

export interface StudioVisualEditorSettingsDropdownOpenedProperties {
  /**
   * ID of an Create organization (while Create and Core organizations are different concepts)
   */
  createOrganizationId?: string;
  /**
   * ID of a Create project
   */
  projectId: string;
}

export interface StudioVisualEditorSettingsOptionClickedProperties {
  /**
   * ID of an Create organization (while Create and Core organizations are different concepts)
   */
  createOrganizationId?: string;
  /**
   * ID of a Create project
   */
  projectId: string;
}

export class CreateTeamOverviewLinkClicked implements BaseEvent {
  event_type = 'create:team_overview_link_clicked';
}

export class CreateTeamOverviewLinkShown implements BaseEvent {
  event_type = 'create:team_overview_link_shown';
}

export class StudioRestartContainerClicked implements BaseEvent {
  event_type = 'studio:restart_container_clicked';

  constructor(
    public event_properties: StudioRestartContainerClickedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class StudioRestartPreviewClicked implements BaseEvent {
  event_type = 'studio:restart_preview_clicked';

  constructor(
    public event_properties: StudioRestartPreviewClickedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class StudioVisualEditorSettingsDropdownOpened implements BaseEvent {
  event_type = 'studio:visual_editor_settings_dropdown_opened';

  constructor(
    public event_properties: StudioVisualEditorSettingsDropdownOpenedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class StudioVisualEditorSettingsOptionClicked implements BaseEvent {
  event_type = 'studio:visual_editor_settings_option_clicked';

  constructor(
    public event_properties: StudioVisualEditorSettingsOptionClickedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: AmplitudeClient;
  private middlewares: Middleware[] = [];

  get client(): AmplitudeClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.isLoaded) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options: LoadOptions): void {
    this.disabled = options.disabled ?? false;

    if (this.isLoaded) {
      console.warn('WARNING: Ampli is already initialized. Ampli.load() should be called once at application startup.');
      return;
    }

    let apiKey: string | null = null;
    if (options.client && 'apiKey' in options.client) {
      apiKey = options.client.apiKey;
    } else if ('environment' in options) {
      apiKey = ApiKey[options.environment];
    }

    if (options.client && 'instance' in options.client) {
      this.amplitude = options.client.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.getInstance();
      this.amplitude?.init(apiKey, undefined, { ...DefaultOptions, ...options.client?.options });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  identify(
    userId: string | undefined,
    options?: IdentifyOptions,
    extra?: MiddlewareExtra
  ) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    const event: IdentifyEvent = {
      event_type: SpecialEventType.Identify,
      user_id: userId || options?.user_id,
      device_id: options?.device_id
    };
    this.runMiddleware({ event, extra }, payload => {
      if (payload.event.user_id) {
        this.amplitude?.setUserId(payload.event.user_id);
      }
      if (payload.event.device_id) {
        this.amplitude?.setDeviceId(payload.event.device_id);
      }

      this._identify(payload.event, options);
    });
  }

  private _identify(
    event: Event,
    options?: IdentifyOptions,
  ) {
    const amplitudeIdentify = new amplitude.Identify();
    if (event.event_properties != null) {
      for (const [key, value] of Object.entries(event.event_properties)) {
        amplitudeIdentify.set(key, value);
      }
    }
    this.amplitude?.identify(
      amplitudeIdentify,
      options?.callback,
      // options?.errorCallback
    );
  }

  setGroup(name: string, value: string | string[], options?: GroupOptions, extra?: MiddlewareExtra) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    this.amplitude?.setGroup(name, value);
  }

  /**
   * Identify a group and set group properties.
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  groupIdentify(
    groupType: string,
    groupName: string | string[],
    options?: GroupOptions,
    extra?: MiddlewareExtra,
  ) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    const event: GroupEvent = {
      event_type: SpecialEventType.Group,
      user_id: options?.user_id,
      device_id: options?.device_id
    };
    this.runMiddleware({ event, extra }, payload => {
      const e = payload.event;
      if (e.user_id) {
        this.amplitude?.setUserId(e.user_id);
      }
      if (e.device_id) {
        this.amplitude?.setDeviceId(e.device_id);
      }
      const amplitudeIdentify = new amplitude.Identify();
      if (e.event_properties != null) {
        for (const [key, value] of Object.entries(e.event_properties)) {
          amplitudeIdentify.set(key, value);
        }
      }
      this.amplitude?.groupIdentify(groupType, groupName, amplitudeIdentify, options?.callback);
    });
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   * @param extra Extra unstructured data for middleware.
   */
  track(event: Event, options?: EventOptions, extra?: MiddlewareExtra) {
    if (!this.isInitializedAndEnabled()) {
      return;
    }

    const trackEvent: BaseEvent = { ...event, ...options };
    this.runMiddleware({ event: trackEvent, extra }, payload => {
      if (payload.event.user_id) {
        this.amplitude?.setUserId(payload.event.user_id);
      }
      if (payload.event.device_id) {
        this.amplitude?.setDeviceId(payload.event.device_id);
      }

      const userProperties = (payload.event as BaseEvent).user_properties;
      if (userProperties) {
        const identifyEvent: IdentifyEvent = {
          event_type: SpecialEventType.Identify,
          event_properties: userProperties,
          user_id: payload.event.user_id,
          device_id: payload.event.device_id
        };
        this._identify(identifyEvent, options);
      }

      this.amplitude?.logEvent(
        payload.event.event_type,
        payload.event.event_properties,
        options?.callback,
        // options?.errorCallback,
      );
    });
  }

  /**
   * create:team_overview_link_clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/create%3Ateam_overview_link_clicked)
   *
   * This event tracks when a user clicks on the "Team Overview" link on the Create dashboard.
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  createTeamOverviewLinkClicked(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new CreateTeamOverviewLinkClicked(), options, extra);
  }

  /**
   * create:team_overview_link_shown
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/create%3Ateam_overview_link_shown)
   *
   * This event tracks when the "Team Overview" link is displayed to users on the Create dashboard
   *
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  createTeamOverviewLinkShown(
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new CreateTeamOverviewLinkShown(), options, extra);
  }

  /**
   * studio:restart_container_clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/studio%3Arestart_container_clicked)
   *
   * Event indicating that a user clicked to restart a container in the visual editor
   *
   * @param properties The event's properties (e.g. createOrganizationId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  studioRestartContainerClicked(
    properties: StudioRestartContainerClickedProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new StudioRestartContainerClicked(properties), options, extra);
  }

  /**
   * studio:restart_preview_clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/studio%3Arestart_preview_clicked)
   *
   * Event indicating that a user clicked to restart the preview in the visual editor
   *
   * @param properties The event's properties (e.g. createOrganizationId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  studioRestartPreviewClicked(
    properties: StudioRestartPreviewClickedProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new StudioRestartPreviewClicked(properties), options, extra);
  }

  /**
   * studio:visual_editor_settings_dropdown_opened
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/studio%3Avisual_editor_settings_dropdown_opened)
   *
   * Event to track when a user opens the settings dropdown in the visual editor
   *
   * @param properties The event's properties (e.g. createOrganizationId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  studioVisualEditorSettingsDropdownOpened(
    properties: StudioVisualEditorSettingsDropdownOpenedProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new StudioVisualEditorSettingsDropdownOpened(properties), options, extra);
  }

  /**
   * studio:visual_editor_settings_option_clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/netlify/Netlify/events/cmparsons%2Fcreate_project_settings/latest/studio%3Avisual_editor_settings_option_clicked)
   *
   * Event to track when a user clicks on the visual editor settings link
   *
   * @param properties The event's properties (e.g. createOrganizationId)
   * @param options Amplitude event options.
   * @param extra Extra untyped parameters for use in middleware.
   */
  studioVisualEditorSettingsOptionClicked(
    properties: StudioVisualEditorSettingsOptionClickedProperties,
    options?: EventOptions,
    extra?: MiddlewareExtra,
  ) {
    return this.track(new StudioVisualEditorSettingsOptionClicked(properties), options, extra);
  }

  addEventMiddleware(middleware: Middleware): void {
    this.middlewares.push(middleware);
  }

  private runMiddleware(payload: MiddlewarePayload, next: MiddlewareNext): void {
    let curMiddlewareIndex = -1;
    const middlewareCount = this.middlewares.length;

    const middlewareNext: MiddlewareNext = curPayload => {
      curMiddlewareIndex += 1;
      if (curMiddlewareIndex < middlewareCount) {
        this.middlewares[curMiddlewareIndex](curPayload, _next);
      } else {
        next(curPayload);
      }
    };

    const _next: MiddlewareNext = middlewareCount > 0 ? middlewareNext : next;

    _next(payload);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type ConfigExt = Partial<Config> & { plan?: Plan };

export type Plan = {
  branch?: string;
  source?: string;
  version?: string;
  versionId?: string;
}

export enum SpecialEventType {
  Identify = "$identify",
  Group = "$groupidentify"
}

export type BaseEvent = {
  event_type: string;
  event_properties?: { [key: string]: any },
  plan?: Plan;
  user_id?: string;
  device_id?: string;
  user_properties?: { [key: string]: any };
}
export type IdentifyEvent = Omit<BaseEvent, 'user_properties'> & {event_type: SpecialEventType.Identify };
export type GroupEvent = Omit<BaseEvent, 'user_properties'> & { event_type: SpecialEventType.Group };
export type Event = BaseEvent | IdentifyEvent | GroupEvent;

type BaseEventOptions = Omit<BaseEvent, 'event_type' | 'event_properties'> & {
  callback?: Callback;
  errorCallback?: Callback;
};
export type EventOptions = BaseEventOptions;
export type IdentifyOptions = Omit<BaseEventOptions, 'user_properties'>;
export type GroupOptions = Omit<BaseEventOptions, 'user_properties'>;

/**
 * Unstructured object to let users pass extra data to middleware
 */
export interface MiddlewareExtra {
  [name: string]: any;
}

/**
 * Data to be processed by middleware
 */
export interface MiddlewarePayload {
  event: Event;
  extra?: MiddlewareExtra;
}

/**
 * Function called at the end of each Middleware to run the next middleware in the chain
 */
export type MiddlewareNext = (payload: MiddlewarePayload) => void;

/**
 * A function to run on the Event stream (each logEvent call)
 *
 * @param payload The event and extra data being sent
 * @param next Function to run the next middleware in the chain, not calling next will end the middleware chain
 */
export type Middleware = (payload: MiddlewarePayload, next: MiddlewareNext) => void;
